import React, { useState, useEffect, useRef } from "react";
import * as echarts from 'echarts';
import DashboardMainContainer from "../common/Containers/DashboardMainContainer";
import { AdminWidgetHeader, AdminWidgetPeriodFilter } from './AdminWidgetChrome';
import { dashboardChartTooltipAxisFormatter } from './dashboardChartTooltip';

const PostEngagementWidget = ({
  className = 'masonry-item w-[440px] h-[237px] rounded-lg flex flex-col',
}) => {
  const [engagementFilterPeriod, setEngagementFilterPeriod] = useState('7 Days');
  const engagementChartRef = useRef(null);

  // Engagement data for different periods
  const engagementData = {
    '7 Days': {
      labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
      views: [90, 110, 140, 100, 130, 100, 130],
      saves: [40, 70, 40, 80, 50, 65, 80],
      shares: [20, 10, 30, 15, 60, 20, 30]
    },
    '14 Days': {
      labels: ['Week 1', 'Week 2'],
      views: [900, 1100],
      saves: [400, 500],
      shares: [200, 250]
    },
    '30 Days': {
      labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4'],
      views: [450, 520, 480, 550],
      saves: [180, 220, 200, 240],
      shares: [90, 110, 95, 120]
    },
    '90 Days': {
      labels: ['Month 1', 'Month 2', 'Month 3'],
      views: [1200, 1400, 1350],
      saves: [600, 700, 680],
      shares: [300, 350, 340]
    },
    '1 Year': {
      labels: ['Q1', 'Q2', 'Q3', 'Q4'],
      views: [3600, 4200, 4050, 4500],
      saves: [1800, 2100, 2025, 2250],
      shares: [900, 1050, 1012, 1125]
    }
  };

  // Initialize chart
  useEffect(() => {
    if (engagementChartRef.current) {
      const chart = echarts.init(engagementChartRef.current);
      const option = {
        backgroundColor: 'transparent',
        tooltip: { 
          trigger: 'axis',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: '#e5e7eb',
          borderWidth: 1,
          textStyle: { color: '#374151' },
          renderMode: 'html',
          formatter: dashboardChartTooltipAxisFormatter,
        },
        legend: {
          data: ['Views', 'Saves', 'Shares'],
          bottom: 0,
          textStyle: { 
            fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
            fontSize: 12,
            fontWeight: 500,
            color: '#232A2E'
          },
          itemGap: 70,
          itemWidth: 35,
          itemHeight: 6,
          icon: 'path://M6,0h50c3.3,0,6,2.7,6,6v0c0,3.3-2.7,6-6,6h-50c-3.3,0-6-2.7-6-6v0c0-3.3,2.7-6,6-6z',
        },
        grid: { 
          top: 2, 
          right: 0, 
          bottom: 40, 
          left: 0,
          backgroundColor: 'transparent',
          containPixel: true
        },
        xAxis: {
          type: 'category',
          data: engagementData[engagementFilterPeriod].labels,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { show: false },
          boundaryGap: false
        },
        yAxis: {
          type: 'value',
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { show: false },
          axisLabel: { show: false }
        },
        series: [
          {
            name: 'Views',
            data: engagementData[engagementFilterPeriod].views,
            type: 'line',
            smooth: true,
            lineStyle: { 
              color: '#7B62D9', 
              width: 3 
            },
            showSymbol: false,
            symbol: 'circle',
            symbolSize: 6,
            itemStyle: { color: '#7B62D9' }
          },
          {
            name: 'Saves',
            data: engagementData[engagementFilterPeriod].saves,
            type: 'line',
            smooth: true,
            lineStyle: { 
              color: '#03A62C', 
              width: 3 
            },
            showSymbol: false,
            symbol: 'circle',
            symbolSize: 6,
            itemStyle: { color: '#03A62C' }
          },
          {
            name: 'Shares',
            data: engagementData[engagementFilterPeriod].shares,
            type: 'line',
            smooth: true,
            lineStyle: { 
              color: '#FB1912', 
              width: 3 
            },
            showSymbol: false,
            symbol: 'circle',
            symbolSize: 6,
            itemStyle: { color: '#FB1912' }
          }
        ]
      };
      chart.setOption(option);
      
      // Cleanup
      return () => chart.dispose();
    }
  }, [engagementFilterPeriod]);

  return (
    <DashboardMainContainer className={className}>
      <AdminWidgetHeader title="Post Engagement Overview">
        <AdminWidgetPeriodFilter
          period={engagementFilterPeriod}
          onPeriodChange={setEngagementFilterPeriod}
        />
      </AdminWidgetHeader>
      <div className="flex-1 p-4">
        <div ref={engagementChartRef} className="w-full h-full"></div>
      </div>
    </DashboardMainContainer>
  );
};

export default PostEngagementWidget;
